/**
 * TicketRating - Satisfaction rating component
 */

import { Card } from '@/components/ui/card';
import { Heading, Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Star } from 'lucide-react';
import { useState } from 'react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';

interface TicketRatingProps {
  currentRating?: number;
  onSubmit: (rating: number) => Promise<boolean>;
  disabled?: boolean;
  className?: string;
}

export function TicketRating({
  currentRating,
  onSubmit,
  disabled = false,
  className,
}: TicketRatingProps) {
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const [selectedRating, setSelectedRating] = useState<number | null>(currentRating ?? null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(currentRating !== undefined);

  const handleSubmit = async (rating: number) => {
    if (disabled || isSubmitting || isSubmitted) return;

    setSelectedRating(rating);
    setIsSubmitting(true);

    const success = await onSubmit(rating);
    if (success) {
      setIsSubmitted(true);
    }

    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <Card className={cn('p-4', semanticTokens.positive.bg, semanticTokens.positive.border, className)}>
        <div className="flex items-center gap-3">
          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", semanticTokens.positive.bg)}>
            <SFIcon icon={sfIcons.check} className={cn("h-4 w-4", semanticTokens.positive.accent)} />
          </div>
          <div>
            <Text className={cn("font-medium", semanticTokens.positive.fg)}>
              Thank you for your feedback!
            </Text>
            <div className="flex gap-0.5 mt-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={cn(
                    'h-4 w-4',
                    star <= (selectedRating || 0)
                      ? 'text-yellow-500 fill-yellow-500'
                      : semanticTokens.fg.disabled
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn('p-4', className)}>
      <Heading variant="h5" className="mb-2">How was your experience?</Heading>
      <Text variant="sm" color="muted" className="mb-4">
        Your feedback helps us improve our support.
      </Text>

      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((rating) => (
          <button
            key={rating}
            onClick={() => handleSubmit(rating)}
            onMouseEnter={() => setHoveredRating(rating)}
            onMouseLeave={() => setHoveredRating(null)}
            disabled={disabled || isSubmitting}
            className={cn(
              'p-2 rounded-lg',
              'transition-all duration-120',
              'hover:scale-110 active:scale-95',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              semanticTokens.focus.ring
            )}
            aria-label={`Rate ${rating} out of 5`}
          >
            <Star
              className={cn(
                'h-8 w-8 transition-colors',
                rating <= (hoveredRating ?? selectedRating ?? 0)
                  ? 'text-yellow-500 fill-yellow-500'
                  : cn(semanticTokens.fg.disabled, 'hover:text-yellow-300')
              )}
            />
          </button>
        ))}
      </div>

      <div className="flex justify-between mt-2">
        <Text variant="xs" color="muted">Poor</Text>
        <Text variant="xs" color="muted">Excellent</Text>
      </div>
    </Card>
  );
}
